import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  VStack,
} from '@chakra-ui/react';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IKanbanColumn } from '../../../../hooks/useKanban';
import { EstatutoDestino, EstatutoStatus, useEstatuto } from '../../../../hooks/useEstatuto';
import { useAuth } from '../../../../providers/auth';

interface EstatutoQuickTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  // Colunas do quadro ativo (etapas onde o processo pode nascer).
  columns: IKanbanColumn[];
  // Quadro ativo (aba): 'constituicao' (Estatuto) ou 'baixa'. Define o destino do processo.
  destino: EstatutoDestino;
  // Projeto do board ativo — p/ invalidar o cache certo após criar.
  projetoId?: number;
}

// Título da coluna do kanban -> etapa_inicial (status). Espelha o ETAPA_INICIAL_PARA_COLUNA
// do backend (estatuto_kanban_sync). Colunas do fluxo normal ("Criado"/"Link Enviado") e a
// terminal ("Assinado") NÃO mapeiam -> undefined (o processo nasce em "Criado"). Só as etapas
// avançadas relaxam cartório/advogado (T12-A), permitindo criação rápida sem esses dados.
const COLUNA_TITULO_PARA_ETAPA: Record<string, EstatutoStatus> = {
  'Aguardando Validação': 'aguardando_validacao',
  'Enviar para Assinatura': 'enviar_assinatura',
};

/**
 * Facilitador de criação RÁPIDA de um ESTATUTO/BAIXA no board.
 *
 * NÃO cria um card kanban genérico — cria um PROCESSO real (`createEstatuto`) com o essencial:
 * nome (título), etapa (coluna) e responsável = usuário logado. Cartório/advogado/empresa ficam
 * vazios (o usuário completa depois no card, editando). É um atalho p/ o EstatutoCreateModal
 * completo. O `destino` (Estatuto/Baixa) vem da aba ativa.
 */
export const EstatutoQuickTaskModal: React.FC<EstatutoQuickTaskModalProps> = ({
  isOpen,
  onClose,
  columns,
  destino,
  projetoId,
}) => {
  const { createEstatuto } = useEstatuto();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [nome, setNome] = useState('');
  const [columnId, setColumnId] = useState<number | ''>('');

  // Ao (re)abrir: limpa os campos e pré-seleciona a primeira etapa (a mais comum p/ criar).
  useEffect(() => {
    if (isOpen) {
      setNome('');
      setColumnId(columns[0]?.id ?? '');
    }
  }, [isOpen, columns]);

  const createMutation = useMutation({
    mutationFn: () => {
      // Traduz a COLUNA escolhida -> etapa_inicial (status) pelo título. Sem mapeamento
      // (Criado/Link Enviado/Assinado) => undefined => processo nasce em "Criado".
      const colTitulo = columns.find(c => c.id === columnId)?.titulo ?? '';
      const etapaInicial = COLUNA_TITULO_PARA_ETAPA[colTitulo];

      return createEstatuto({
        titulo: nome.trim(),
        destino,
        etapa_inicial: etapaInicial,
        // Backend exige ≥1 responsável — atribui o usuário logado por padrão.
        responsavel_ids: user?.id ? [user.id] : [],
      });
    },
    onSuccess: () => {
      if (projetoId) {
        queryClient.invalidateQueries({ queryKey: ['kanban-board', projetoId] });
      }
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto-dashboard'] });
      toast.success(destino === 'baixa' ? 'Baixa criada' : 'Estatuto criado');
      onClose();
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || 'Erro ao criar o processo'),
  });

  const podeSalvar = nome.trim().length > 0 && columnId !== '' && !createMutation.isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="20px">
        <ModalHeader fontSize="md" fontWeight="700">
          {destino === 'baixa' ? 'Nova baixa (rápida)' : 'Novo estatuto (rápido)'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* 1. Nome / título do processo */}
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                Nome
              </FormLabel>
              <Input
                size="sm"
                borderRadius="md"
                placeholder="Ex.: 2028.2 UNIFIMES TRD MEDICINA 10"
                value={nome}
                onChange={e => setNome(e.target.value)}
                autoFocus
              />
            </FormControl>

            {/* 2. Etapa (coluna do kanban) */}
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                Etapa
              </FormLabel>
              <Select
                size="sm"
                borderRadius="md"
                value={columnId}
                onChange={e => setColumnId(e.target.value ? Number(e.target.value) : '')}
                placeholder="Selecione a etapa"
              >
                {columns.map(col => (
                  <option key={col.id} value={col.id}>
                    {col.titulo}
                  </option>
                ))}
              </Select>
              <FormLabel fontSize="11px" color="gray.400" fontWeight="400" mt={1} mb={0}>
                Cartório, advogado e empresa você preenche depois, editando o card.
              </FormLabel>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            size="sm"
            colorScheme="purple"
            isDisabled={!podeSalvar}
            isLoading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            Criar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
